import { BprAdoption, VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from "./voyage-routing-model";
import type { BprRejectNotificationData, RouteSelectionNotificationData } from "../services/vessel-notifications-service";

// Notification payloads captured at send-and-approve and attached to
// plan.metadata so they travel with the plan (and to shore) instead of
// requiring a join against the notifications service.
export interface CapturedPlanNotifications {
    bprReject?: BprRejectNotificationData;
    routeSelection?: RouteSelectionNotificationData;
}

export type PlanChangedEvent = CustomEvent<{scheduleId: string}>

export enum PlanEvents {
    TOGGLE_POLAR_PLOT_MARKER = "toggle-polar-plot-marker"
}
export interface PassagePlanDraft
{
    foreDraft: number|null;
    aftDraft: number|null;
    airDraft: number|null;
    swDensity: number|null;
}

export interface DraftsType 
{
    berthToPilot: PassagePlanDraft;
    pilotToPilot: PassagePlanDraft;
    pilotToBerth: PassagePlanDraft;
}

export interface PassagePlanSquat
{
    openWaters: number|null;
    confinedWaters: number|null;
}

export interface PassagePlanUKC
{
    required: string;
    minimumExpected: number|null;
}

export interface PassagePlanBridgeTeam
{
    master: boolean;
    chiefOfficer: boolean;
    secondOfficer: boolean;
    thirdOfficer: boolean;
    pilot: boolean;
    helmsman: boolean;
    lookout1: boolean;
    lookout2: boolean;
}

// Plan-level user-input section persisted inside passagePlan.
// The backend stores the passagePlan blob verbatim, so adding fields
// here lets us round-trip data the typed plan API doesn't know about.
export interface PassagePlanUserInput
{
    loadstarSourceMask?: number;
}

export interface BestPracticeRoute {
    available: boolean;
    selected:  boolean;
    reason:    string;
    comment:   string;
    route:     VoyageRoute | null;
}; 

export type PlanStatus = "planning"|"approved";

export interface VoyagePlan
{
    id?: number;
    imoNo?: number;
    routeId?: string|null;
    routeIdParent?: string|null;
    routeIdInitial?: string|null;
    source?: string;
    audit_id?: string;
    scheduleId: string;
    etd: number;
    eta: number;
    departureTerminalCode: string;
    startPosition: [number, number]|null;
    departurePilotPosition: [number, number]|null;
    departurePilotEtd: number;
    arrivalTerminalCode: string;
    endPosition: [number, number]|null;
    arrivalPilotPosition: [number, number]|null;
    arrivalPilotEta: number;
    foreDraft: number;
    aftDraft: number;
    gmt: number|null;
    numberOfReefers: number;
    solidGm?: number|null;
    rollAngle?: number|null;
    transverseAccelerationThreshold?: number|null;
    status: PlanStatus;
    passagePlan: {
        description: string;
        departurePort: string;
        arrivalPort: string;
        portMemo: string;
        reminders: string;
        notEcdisCompliant: boolean;
        encCellsUsed: string;
        booksAndTables: string;
        chartsAndLightsNotes: string;
        sailingDirections: string;
        listOfRadioSignals: string;
        listOfLights: string;
        gpsEcdisRadarMap: string;
        paperCharts: string;
        pubCorrectedToWeek: string;
        tideTables: string;
        publicationsNotes: string;
        captain: string;
        navigationalOfficer: string;
        planDate: string | null;
        positionValidation: string;
        mastersOrders: string;
        drafts: DraftsType;
        squats: {
            minSpeed: PassagePlanSquat;
            maxSpeed: PassagePlanSquat;
            bufferSpeed: PassagePlanSquat;
            vesselMaxSpeed: PassagePlanSquat;
        };
        underKeelClearance: {
            confinedWater: PassagePlanUKC;
            openWater: PassagePlanUKC;
            alongsideDeparture: PassagePlanUKC;
            alongsideArrival: PassagePlanUKC;
        };
        safetyMessages: {
            navareaRegions: string;
            navtexStations: string;
        };
        weatherInfo: {
            navareaRegions: string;
            navtexStations: string;
            info: string;
        };
        equipmentStatus: {
            operationalCondition: string;
            failures: string;
        };
        bridgeTeam: {
            departure: PassagePlanBridgeTeam;
            arrival: PassagePlanBridgeTeam;
        };
        calculatedValues: {
            avgSpeedOGPilotToPilot: number|null;
            routeMinSpeed: number|null;
            routeMaxSpeed: number|null;
            routeAverageSpeed: number|null;
            berthToPilotDistance: number|null;
            pilotToPilotDistance: number|null;
            pilotToBerthDistance: number|null;
        };
        reduced_propulsion_power?:{
            value: number;
            source: string;
        }
        signatures: Signature[],      
        bestPracticeRoute?: BestPracticeRoute;
        userInput?: PassagePlanUserInput;
    };
    routes: VoyageRoute[];
    selectedRouteIndex: number;
    autoUpdateReason: string;
    create_dtm?: string;
    update_dtm?: string;
    metadata?: {
        speed?: number;
        bprReject?: BprRejectNotificationData;
        routeSelection?: RouteSelectionNotificationData;
    },
    currentSelectedRouteId?: string;
    wr_enabled?: boolean;
}

export interface Signature
{
    rank: string;
    name: string;
}

export enum LegSpeedSettings {
    CALCULATED_SPEED = 'calculated_speed',
    MAX_SPEED = "max_speed"
}

export interface VesselCharacteristics
{
    height: number;
    minSpeed: number; // in m/s
    maxSpeed: number; // in m/s
    blockCoefficient: number;
    beamSize: number;
    ecdisWPStartIndex: number;
    legSpeedSetting: LegSpeedSettings
}

// Port pair context used to resolve the top-1 star route when no bprID is supplied (e.g. route upload).
export interface RouteAdoptionPortPair {
    departureTerminalCode: string;
    arrivalTerminalCode: string;
    scheduleId: string;
}

export interface RouteAdoptionResult {
    adopted: boolean;
    // The star route the deviation was measured against (top-1 when resolved server-side).
    bprId: string;
    // The route echoed back with per-waypoint deviation annotations (bpr_deviation_exceeds).
    route: VoyageRoute | null;
}

export abstract class VesselPlanningModelBase extends EventTarget
{
    abstract getPlans(scheduleId: string): Promise<VoyagePlan[]>;
    abstract getAllPlans(): Promise<VoyagePlan[]>;
    abstract getCurrentPlan(scheduleId: string): Promise<VoyagePlan|null>;
    abstract getApprovedPlan(scheduleId: string): Promise<VoyagePlan|null>;
    abstract getAllPlansStatus(): Promise<{[index:string]: PlanStatus}>;
    abstract getExecutingPlan(): Promise<VoyagePlan|null>;
    abstract hasPlan(scheduleId: string): Promise<boolean>;
    abstract createPlan(scheduleId: string): Promise<VoyagePlan>;
    abstract setPlan(scheduleId: string, plan: VoyagePlan, sendToShore: boolean, isWrManualUpdateRequest?: boolean): Promise<void>;
    abstract calculatePlan(plan: VoyagePlan): Promise<VoyagePlan>;
    abstract approvePlan(scheduleId: string, hasPilotEtaChanged?: boolean, capturedNotifications?: CapturedPlanNotifications): Promise<void>;
    abstract recalculatePlan(scheduleId: string, plan: VoyagePlan): Promise<void>;
    abstract getVesselCharacteristics(): Promise<VesselCharacteristics|null>;
    abstract setVesselCharacteristics(characteristics: VesselCharacteristics): Promise<void>;
    abstract isAlignedRoute(route: VoyageRoute, bprID: string ): Promise<boolean>;

    // Returns the adoption verdict plus the deviation-annotated route so callers can persist and render it.
    abstract getRouteAdoption(route: VoyageRoute, bprID?: string, portPair?: RouteAdoptionPortPair, bprRoute?: VoyageRoute): Promise<RouteAdoptionResult>;

    abstract passagePlanChanged(oldPlan: VoyagePlan, newPlan: VoyagePlan): boolean;
    abstract passagePlanRouteChanged(oldRoute: Array<VoyageRouteLineFeature|VoyageRoutePointFeature>,
                                     newRoute: Array<VoyageRouteLineFeature|VoyageRoutePointFeature>): boolean;

    abstract approvalReminder(message: string, autoApprove?: boolean): void;
    abstract _appovePlanClf(): void;
    abstract _cancelPlanClf(): void;
    abstract _savePassagePlan(): void;
    abstract _localPassageUpdated(): void;
    abstract getPlanSizeInMB(plan: VoyagePlan): Promise<number>;
    abstract updateRouteBprAdoption(scheduleId: string, routeId: string, bprAdoption: BprAdoption): Promise<void>;
}

let VesselPlanningModel: VesselPlanningModelBase|null = null;

function setVesselPlanningModel(model: VesselPlanningModelBase)
{
    VesselPlanningModel = model;
}

export { VesselPlanningModel, setVesselPlanningModel };
